import { type AuthTokensResponse, type MeResponse, type PermissionCode } from '@aps/contracts';

/**
 * Xodim sessiyasi.
 *
 * Tokenlar `sessionStorage` da saqlanadi, `localStorage` da EMAS:
 *
 * - `localStorage` brauzer yopilgandan keyin ham qoladi. Umumiy
 *   kompyuterdagi sotuv menejeri uchun bu haqiqiy xavf.
 * - Ikkalasi ham XSS ga ochiq. To'g'ri yechim — `httpOnly` cookie, lekin
 *   API hozir `Bearer` sxemasida ishlaydi. Buni almashtirish alohida
 *   topshiriq (TASK-008 doirasidan tashqarida) va u yozib qo'yilgan:
 *   docs/01-ARXITEKTURA.md → "Qolgan xavf".
 *
 * `sessionStorage` tanlovi xavfni yo'q qilmaydi, uning OYNASINI qisqartiradi.
 */
const STORAGE_KEY = 'aps.admin.session';

export interface AdminSession {
  tokens: AuthTokensResponse;
  me: MeResponse;
}

export function loadSession(): AdminSession | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private rejim yoki bloklangan storage — sessiya shunchaki saqlanmaydi.
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSession(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: AdminSession | null): void {
  try {
    if (session === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Saqlash imkoni bo'lmasa ham ilova ishlashda davom etadi.
  }
}

/**
 * Saqlangan qiymatni TEKSHIRIB o'qiydi.
 *
 * `sessionStorage` ni foydalanuvchi ham, boshqa skript ham o'zgartira
 * oladi. Tekshirmasdan `as AdminSession` yozish — ilovani birinchi
 * nuqtadan yiqitadigan yo'l.
 */
function isSession(value: unknown): value is AdminSession {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;

  const tokens = record['tokens'];
  if (typeof tokens !== 'object' || tokens === null) return false;
  const tokenRecord = tokens as Record<string, unknown>;
  if (typeof tokenRecord['accessToken'] !== 'string') return false;
  if (typeof tokenRecord['refreshToken'] !== 'string') return false;

  const me = record['me'];
  if (typeof me !== 'object' || me === null) return false;
  const meRecord = me as Record<string, unknown>;
  if (typeof meRecord['id'] !== 'string') return false;
  if (!Array.isArray(meRecord['permissions'])) return false;

  return true;
}

/**
 * Ruxsat tekshiruvi — FAQAT ekranni yashirish uchun.
 *
 * Haqiqiy chegara serverda: `PermissionsGuard` har so'rovda tekshiradi.
 * Bu yerdagi tekshiruv foydalanuvchiga bosib bo'lmaydigan tugmani
 * ko'rsatmaslik uchun, xavfsizlik uchun emas.
 */
export function can(me: MeResponse, permission: PermissionCode): boolean {
  return me.permissions.includes(permission);
}
